import { lazy, useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Hero from "@/components/Hero";
import DeferredSection from "@/components/DeferredSection";
import { Reveal, Overline } from "@/components/Reveal";
import { withBrand } from "@/components/Brand";
import { useProducts } from "@/context/ProductsContext";
import { useSession } from "@/context/SessionContext";
import { scrollToId } from "@/lib/scroll";
import { pTextFlag } from "@/i18n/translations";
import { withMarketPath } from "@/lib/marketRoutes";
import { HOME_SECTION_INTENT_EVENT } from "@/lib/homeSections";
import HomeCampaignBadge from "@/components/HomeCampaignBadge";

const loadStatsDrum = () => import("@/components/StatsDrum");
const loadMarquee = () => import("@/components/Marquee");
const loadManifesto = () => import("@/components/Manifesto");
const loadHowItWorks = () => import("@/components/HowItWorks");
const loadHomeProducts = () => import("@/components/HomeProductsSection");
const loadBrochureSection = () => import("@/components/BrochureSection");
const loadFaqSection = () => import("@/components/FaqSection");
const loadQuoteForm = () => import("@/components/QuoteForm");

const StatsDrum = lazy(loadStatsDrum);
const Marquee = lazy(loadMarquee);
const Manifesto = lazy(loadManifesto);
const HowItWorks = lazy(loadHowItWorks);
const HomeProductsSection = lazy(loadHomeProducts);
const BrochureSection = lazy(loadBrochureSection);
const FaqSection = lazy(loadFaqSection);
const QuoteForm = lazy(loadQuoteForm);

const homeSectionLoaders = {
  saadan: loadManifesto,
  paafyldning: loadHowItWorks,
  emballager: loadHomeProducts,
  brochure: loadBrochureSection,
  faq: loadFaqSection,
  kontakt: loadQuoteForm,
};

export default function HomePage({ withAgentNotice = false }) {
  const location = useLocation();
  const marketPath = (path) => withMarketPath(location.pathname, path);
  const { products } = useProducts();
  const { t, productTexts } = useSession();
  const [forcedSections, setForcedSections] = useState({});
  const homeProducts = products
    .filter((product) => pTextFlag(product, "show_forside", productTexts))
    .sort((a, b) => String(a.product_id || "").localeCompare(String(b.product_id || ""), undefined, { numeric: true }));

  const prepareHomeSection = useCallback((id, action = "activate") => {
    const loader = homeSectionLoaders[id];
    if (loader) void loader();
    if (action === "activate" && loader) {
      setForcedSections((current) => (current[id] ? current : { ...current, [id]: true }));
    }
  }, []);

  useEffect(() => {
    const onSectionIntent = ({ detail }) => prepareHomeSection(detail?.id, detail?.action);
    window.addEventListener(HOME_SECTION_INTENT_EVENT, onSectionIntent);
    return () => window.removeEventListener(HOME_SECTION_INTENT_EVENT, onSectionIntent);
  }, [prepareHomeSection]);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      prepareHomeSection(id);
      const t = setTimeout(() => scrollToId(id, { settle: true }), 0);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [location.hash, prepareHomeSection]);

  return (
    <main data-testid="home-page">
      <Hero withAgentNotice={withAgentNotice} />
      <HomeCampaignBadge />

      <section id="forebyg-reparer" data-testid="forebyg-reparer" className="relative grid-pattern py-20 sm:py-24 border-t border-brand-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight leading-[0.95] max-w-4xl">
              {t("home.h2a")} <span className="text-brand-blue">{t("home.h2b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed">
              {withBrand(t("home.p1"))}
            </p>
            <p className="mt-5 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed">
              {withBrand(t("home.p2"))}
            </p>
          </Reveal>
        </div>
      </section>

      <DeferredSection testId="deferred-stats-drum" minHeight="38rem"><StatsDrum /></DeferredSection>
      <DeferredSection testId="deferred-marquee" minHeight="5rem"><Marquee /></DeferredSection>
      <DeferredSection id="saadan" forceLoad={forcedSections.saadan} testId="deferred-manifesto" minHeight="31rem"><Manifesto /></DeferredSection>
      <DeferredSection id="paafyldning" forceLoad={forcedSections.paafyldning} testId="deferred-how-it-works" minHeight="42rem"><HowItWorks /></DeferredSection>

      <section id="maengde" data-testid="maengde-section" className="py-20 sm:py-24 border-t border-brand-border bg-[#0D1117]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Reveal>
            <Overline>{t("home.mover")}</Overline>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight mt-4 leading-[0.95] max-w-3xl">
              {t("home.mh2a")} <span className="text-brand-blue">{t("home.mh2b")}</span>
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to={marketPath("/hvor-meget-skal-mit-daek")}
              data-testid="maengde-page-btn"
              className="group mt-8 inline-flex items-center gap-3 font-display text-base font-bold uppercase tracking-widest bg-brand-blue text-[#0B0E12] px-8 py-4 hover:bg-white transition-colors"
            >
              {t("home.mbtn")}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section id="emballager" data-testid="products-preview" className="py-28 sm:py-36 border-t border-brand-border">
        <DeferredSection forceLoad={forcedSections.emballager} testId="deferred-home-products" minHeight="58rem"><HomeProductsSection products={homeProducts} t={t} /></DeferredSection>
      </section>

      <DeferredSection id="brochure" forceLoad={forcedSections.brochure} testId="deferred-brochure" minHeight="34rem"><BrochureSection /></DeferredSection>
      <DeferredSection id="faq" forceLoad={forcedSections.faq} testId="deferred-faq" minHeight="44rem"><FaqSection /></DeferredSection>
      <DeferredSection id="kontakt" forceLoad={forcedSections.kontakt} testId="deferred-quote" minHeight="34rem"><QuoteForm /></DeferredSection>
    </main>
  );
}
